import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Box,
  Typography,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";

// Import components
import FormationView from "./components/FormationView";
import RosterDrawer from "./components/RosterDrawer";
import api from "../../utils/api";

// Simplified position definitions
const OFFENSE_POSITIONS = [
  { id: "qb", name: "Quarterback", abbreviation: "QB" },
  { id: "rb", name: "Running Back", abbreviation: "RB" },
  { id: "wr1", name: "Wide Receiver 1", abbreviation: "WR1" },
  { id: "wr2", name: "Wide Receiver 2", abbreviation: "WR2" },
  { id: "te", name: "Tight End", abbreviation: "TE" },
  { id: "lt", name: "Left Tackle", abbreviation: "LT" },
  { id: "lg", name: "Left Guard", abbreviation: "LG" },
  { id: "c", name: "Center", abbreviation: "C" },
  { id: "rg", name: "Right Guard", abbreviation: "RG" },
  { id: "rt", name: "Right Tackle", abbreviation: "RT" },
];

const DEFENSE_POSITIONS = [
  { id: "dt", name: "Defensive Tackle", abbreviation: "DT" },
  { id: "de", name: "Defensive End", abbreviation: "DE" },
  { id: "mlb", name: "Middle Linebacker", abbreviation: "MLB" },
  { id: "olb", name: "Outside Linebacker", abbreviation: "OLB" },
  { id: "cb", name: "Cornerback", abbreviation: "CB" },
  { id: "fs", name: "Free Safety", abbreviation: "FS" },
  { id: "ss", name: "Strong Safety", abbreviation: "SS" },
];

// TabPanel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`depth-chart-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const DepthChartEditor = () => {
  // Simplified state
  const [tabValue, setTabValue] = useState(0);
  const [rosterDrawerOpen, setRosterDrawerOpen] = useState(false);
  const [players, setPlayers] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
  });
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // Depth Chart Management State
  const [depthCharts, setDepthCharts] = useState([]);
  const [currentDepthChart, setCurrentDepthChart] = useState(null);
  const [chartName, setChartName] = useState("New Depth Chart");
  const [season, setSeason] = useState("2024-2025");
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [isNewChart, setIsNewChart] = useState(true);

  const showNotification = useCallback((message, severity = "info") => {
    setNotification({ open: true, message, severity });
  }, []);

  const loadData = useCallback(
    async (page = 1, append = false) => {
      try {
        setLoading(true);

        // Load prospects from database with pagination
        const prospectsResponse = await api.get(
          `/prospects?page=${page}&limit=${pagination.limit}`
        );

        // Handle different response structures
        let prospectsData = [];
        let paginationInfo = {};

        if (prospectsResponse.success && prospectsResponse.data) {
          prospectsData = prospectsResponse.data;
          paginationInfo = {
            currentPage: page,
            limit: prospectsResponse.count || pagination.limit,
            total: prospectsResponse.total || 0,
            totalPages: Math.ceil(
              (prospectsResponse.total || 0) / pagination.limit
            ),
          };
        } else if (Array.isArray(prospectsResponse.data)) {
          prospectsData = prospectsResponse.data;
          paginationInfo = {
            currentPage: page,
            limit: pagination.limit,
            total: prospectsResponse.data.length,
            totalPages: 1,
          };
        } else {
          console.error(
            "❌ Failed to load prospects - Invalid response format"
          );
          prospectsData = [];
        }

        // Update prospects - append if loading more, replace if new page
        if (append && page > 1) {
          setProspects((prev) => [...prev, ...prospectsData]);
        } else {
          setProspects(prospectsData);
        }

        // Update pagination info
        setPagination(paginationInfo);

        // Load existing depth charts (only on first load)
        if (page === 1) {
          const depthChartsResponse = await api.get("/depthcharts");

          if (
            depthChartsResponse.success &&
            Array.isArray(depthChartsResponse.data)
          ) {
            setDepthCharts(depthChartsResponse.data);
          } else {
            console.error(
              "❌ Failed to load depth charts - Invalid response format"
            );
            setDepthCharts([]);
          }
        }
      } catch (error) {
        console.error("Error loading data:", error);
        showNotification("Error loading data", "error");
      } finally {
        setLoading(false);
      }
    },
    [showNotification, pagination.limit]
  );

  // Load prospects and current roster
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Save depth chart to backend
  const handleSaveDepthChart = async () => {
    try {
      setSaving(true);

      // Prepare depth chart data
      const depthChartData = {
        name: chartName,
        season: season,
        positions: [...OFFENSE_POSITIONS, ...DEFENSE_POSITIONS].map((pos) => ({
          positionId: pos.id,
          positionName: pos.name,
          abbreviation: pos.abbreviation,
          group:
            pos.id.includes("qb") ||
            pos.id.includes("rb") ||
            pos.id.includes("wr") ||
            pos.id.includes("te") ||
            pos.id.includes("lt") ||
            pos.id.includes("lg") ||
            pos.id.includes("c") ||
            pos.id.includes("rg") ||
            pos.id.includes("rt")
              ? "Offense"
              : "Defense",
          maxDepth: 3,
          requiredCount: 1,
        })),
        players: players.map((player) => ({
          playerId: player.playerId || player.id, // Use playerId first, fallback to id
          positionId: player.position,
          depth: player.depth,
          isStarter: player.depth === 1,
          notes: player.notes || "",
        })),
        isActive: true,
      };

      // Check if no players are assigned
      if (depthChartData.players.length === 0) {
        showNotification(
          "Warning: No players are assigned to this depth chart. The chart will be saved but will be empty.",
          "warning"
        );
      }

      let response;
      if (isNewChart || !currentDepthChart) {
        // Create new depth chart
        response = await api.post("/depthcharts", depthChartData);
        setCurrentDepthChart(response.data.data);
        setIsNewChart(false);
        showNotification(
          `Depth chart "${chartName}" created successfully!`,
          "success"
        );
      } else {
        // Update existing depth chart
        response = await api.put(
          `/depthcharts/${currentDepthChart._id}`,
          depthChartData
        );
        setCurrentDepthChart(response.data.data);
        showNotification(
          `Depth chart "${chartName}" updated successfully!`,
          "success"
        );
      }

      // Refresh the depth charts list
      loadData();
    } catch (error) {
      console.error("Error saving depth chart:", error);
      showNotification("Error saving depth chart", "error");
    } finally {
      setSaving(false);
    }
  };

  // Load a specific depth chart
  const handleLoadDepthChart = async (depthChartId) => {
    try {
      setLoading(true);
      const response = await api.get(`/depthcharts/${depthChartId}`);

      // Handle different response structures
      let depthChart;
      if (response.success && response.data) {
        depthChart = response.data;
      } else if (response.data && response.data.success && response.data.data) {
        depthChart = response.data.data;
      } else if (response.data && response.data.name) {
        depthChart = response.data;
      } else {
        throw new Error("Invalid response structure");
      }

      if (!depthChart || !depthChart.name) {
        throw new Error("Depth chart data is missing or invalid");
      }

      setCurrentDepthChart(depthChart);
      setChartName(depthChart.name);
      setSeason(depthChart.season || "2024-2025");
      setIsNewChart(false);

      // Load players from the depth chart with error handling
      const loadedPlayers = [];
      if (depthChart.players && Array.isArray(depthChart.players)) {
        for (const player of depthChart.players) {
          try {
            // Check if playerId is populated or just an ID
            if (player.playerId && typeof player.playerId === "object") {
              // Fully populated player object
              loadedPlayers.push({
                id: player.playerId._id,
                playerId: player.playerId._id,
                name: `${player.playerId.firstName} ${player.playerId.lastName}`,
                number: player.playerId.jerseyNumber,
                position: player.positionId,
                depth: player.depth,
                year: player.playerId.classYear,
                height: player.playerId.height,
                weight: player.playerId.weight,
                hometown: player.playerId.hometown,
              });
            } else if (player.playerId) {
              // Just ID reference - create basic player object
              loadedPlayers.push({
                id: player.playerId,
                playerId: player.playerId,
                name: `Player ${player.playerId}`,
                number: "??",
                position: player.positionId,
                depth: player.depth,
                year: "Unknown",
                height: "",
                weight: "",
                hometown: "",
              });
            }
          } catch (playerError) {
            console.warn("⚠️ Error processing player:", player, playerError);
          }
        }
      }

      setPlayers(loadedPlayers);
      showNotification(
        `Loaded depth chart "${depthChart.name}" with ${loadedPlayers.length} players`,
        "success"
      );
    } catch (error) {
      console.error("❌ Error loading depth chart:", error);
      showNotification(`Error loading depth chart: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  // Create new depth chart
  const handleNewDepthChart = () => {
    setCurrentDepthChart(null);
    setChartName("New Depth Chart");
    setSeason("2024-2025");
    setPlayers([]);
    setIsNewChart(true);
    showNotification("Started new depth chart", "info");
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleAddPlayerToPosition = async (player, positionId, depth = 1) => {
    try {
      // Check if player is already in this position at this depth
      const existingPlayer = players.find(
        (p) =>
          p.playerId === player._id &&
          p.position === positionId &&
          p.depth === depth
      );

      if (existingPlayer) {
        showNotification(
          `${player.firstName} ${
            player.lastName
          } is already assigned to ${positionId.toUpperCase()} at ${
            depth === 1 ? "1st" : depth === 2 ? "2nd" : "3rd"
          } string`,
          "warning"
        );
        return;
      }

      // Create a player assignment
      const newPlayer = {
        id: player._id,
        playerId: player._id,
        name: `${player.firstName} ${player.lastName}`,
        number: player.jerseyNumber,
        position: positionId,
        depth: depth,
        year: player.classYear,
        height: player.height,
        weight: player.weight,
        hometown: player.hometown,
      };

      // Add to players array
      setPlayers((prev) => [...prev, newPlayer]);

      showNotification(
        `Added ${newPlayer.name} to ${positionId.toUpperCase()} (${
          depth === 1 ? "1st" : depth === 2 ? "2nd" : "3rd"
        } string)`,
        "success"
      );
    } catch (error) {
      console.error("Error adding player:", error);
      showNotification("Error adding player to position", "error");
    }
  };

  const handleEditPlayer = (player) => {
    // For now, just show a notification - could open a modal later
    showNotification(
      `Edit functionality for ${player.name} - coming soon!`,
      "info"
    );
  };

  const handleDeletePlayer = (playerId) => {
    const player = players.find((p) => p.id === playerId);
    if (player) {
      setPlayers((prev) => prev.filter((p) => p.id !== playerId));
      showNotification(`Removed ${player.name} from depth chart`, "info");
    }
  };

  const handleRemovePlayer = (playerId) => {
    setPlayers((prev) => prev.filter((p) => p.id !== playerId));
    showNotification("Player removed from depth chart", "info");
  };

  const handleMovePlayer = (
    playerId,
    sourcePosition,
    sourceDepth,
    targetPosition,
    targetDepth
  ) => {
    setPlayers((prev) =>
      prev.map((player) =>
        player.id === playerId
          ? { ...player, position: targetPosition, depth: targetDepth }
          : player
      )
    );
    showNotification("Player moved to new position", "info");
  };

  const handleAddToRoster = async (player) => {
    try {
      // Add player to current roster
      await api.put(`/prospects/${player._id}`, {
        ...player,
        isCurrentRoster: true,
      });

      showNotification(
        `${player.firstName} ${player.lastName} added to current roster`,
        "success"
      );

      // Refresh data
      loadData();
    } catch (error) {
      console.error("Error adding player to roster:", error);
      showNotification("Error adding player to roster", "error");
    }
  };

  // Pagination functions
  const handleLoadMore = () => {
    if (pagination.currentPage < pagination.totalPages) {
      loadData(pagination.currentPage + 1, true);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      loadData(newPage, false);
    }
  };

  const getFormationPlayers = (unit) => {
    const positions =
      unit === "offense" ? OFFENSE_POSITIONS : DEFENSE_POSITIONS;
    return players.filter((player) =>
      positions.some((pos) => pos.id === player.position)
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Depth Chart Editor
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {chartName} • Season: {season} • {players.length} players assigned •
            Mode: {isNewChart ? "NEW CHART" : "EDITING"} • Chart ID:{" "}
            {currentDepthChart?._id || "None"}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* Roster Sidebar Button */}
          <Tooltip title="Open Player Roster">
            <IconButton
              color="primary"
              onClick={() => setRosterDrawerOpen(true)}
              sx={{
                bgcolor: "primary.main",
                color: "white",
                "&:hover": { bgcolor: "primary.dark" },
              }}
            >
              <PeopleIcon />
            </IconButton>
          </Tooltip>

          {/* Depth Chart Selector */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Select Depth Chart</InputLabel>
            <Select
              value={currentDepthChart?._id || ""}
              onChange={(e) => {
                if (e.target.value === "new") {
                  handleNewDepthChart();
                } else {
                  handleLoadDepthChart(e.target.value);
                }
              }}
              label="Select Depth Chart"
            >
              <MenuItem value="new">
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <AddIcon fontSize="small" sx={{ mr: 1 }} />
                  Create New Depth Chart
                </Box>
              </MenuItem>
              {depthCharts.length === 0 && (
                <MenuItem disabled>No depth charts found</MenuItem>
              )}
              {depthCharts.map((chart) => (
                <MenuItem key={chart._id} value={chart._id}>
                  {chart.name} ({chart.season})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Save Button */}
          <Tooltip
            title={isNewChart ? "Save New Depth Chart" : "Update Depth Chart"}
          >
            <span>
              <IconButton
                color="primary"
                onClick={() => {
                  if (
                    isNewChart &&
                    players.length > 0 &&
                    depthCharts.length > 0
                  ) {
                    // If they have players assigned but no chart selected, offer to update existing
                    const shouldUpdate = window.confirm(
                      `You have ${players.length} players assigned but no depth chart selected.\n\nWould you like to UPDATE an existing chart instead of creating a new one?\n\nClick OK to choose an existing chart to update, or Cancel to create a new chart.`
                    );

                    if (shouldUpdate) {
                      // Open dropdown or load first chart
                      const firstChart = depthCharts[0];
                      handleLoadDepthChart(firstChart._id);
                      return;
                    } else {
                      setSaveDialogOpen(true);
                    }
                  } else if (isNewChart) {
                    setSaveDialogOpen(true);
                  } else {
                    handleSaveDepthChart();
                  }
                }}
                disabled={saving}
                sx={{
                  bgcolor: "primary.main",
                  color: "white",
                  "&:hover": { bgcolor: "primary.dark" },
                  "&:disabled": { bgcolor: "action.disabled" },
                }}
              >
                {saving ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SaveIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Main Content */}
      {!loading && (
        <>
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Offense" />
              <Tab label="Defense" />
            </Tabs>
          </Box>

          {/* Formation Views */}
          <TabPanel value={tabValue} index={0}>
            <FormationView
              unit="offense"
              positions={OFFENSE_POSITIONS}
              players={getFormationPlayers("offense")}
              onAddPlayer={(positionId, depth) => {
                setRosterDrawerOpen(true);
                // Store the position and depth for when a player is selected
                // For now, just open the drawer
              }}
              onRemovePlayer={handleRemovePlayer}
              onDeletePlayer={handleDeletePlayer}
              onEditPlayer={handleEditPlayer}
              onMovePlayer={handleMovePlayer}
              onToggleStarter={() => {}}
              onViewDetails={() => {}}
              maxPlayersPerPosition={3}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <FormationView
              unit="defense"
              positions={DEFENSE_POSITIONS}
              players={getFormationPlayers("defense")}
              onAddPlayer={(positionId, depth) => {
                setRosterDrawerOpen(true);
                // Store the position and depth for when a player is selected
                // For now, just open the drawer
              }}
              onRemovePlayer={handleRemovePlayer}
              onDeletePlayer={handleDeletePlayer}
              onEditPlayer={handleEditPlayer}
              onMovePlayer={handleMovePlayer}
              onToggleStarter={() => {}}
              onViewDetails={() => {}}
              maxPlayersPerPosition={3}
            />
          </TabPanel>
        </>
      )}

      {/* Roster Drawer */}
      <RosterDrawer
        open={rosterDrawerOpen}
        onClose={() => setRosterDrawerOpen(false)}
        players={prospects}
        onAddPlayer={handleAddPlayerToPosition}
        onRefresh={loadData}
        loading={loading}
        depthChartPlayers={players}
        onAddToRoster={handleAddToRoster}
        pagination={pagination}
        onLoadMore={handleLoadMore}
        onPageChange={handlePageChange}
        availablePositions={[...OFFENSE_POSITIONS, ...DEFENSE_POSITIONS].map(
          (pos) => ({
            positionId: pos.id,
            positionName: pos.name,
            abbreviation: pos.abbreviation,
            group:
              pos.id.includes("qb") ||
              pos.id.includes("rb") ||
              pos.id.includes("wr") ||
              pos.id.includes("te") ||
              pos.id.includes("lt") ||
              pos.id.includes("lg") ||
              pos.id.includes("c") ||
              pos.id.includes("rg") ||
              pos.id.includes("rt")
                ? "Offense"
                : "Defense",
          })
        )}
      />

      {/* Save Dialog */}
      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Save Depth Chart</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Depth Chart Name"
              value={chartName}
              onChange={(e) => setChartName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Season"
              value={season}
              onChange={(e) => setSeason(e.target.value)}
              placeholder="e.g., 2024-2025"
              fullWidth
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              setSaveDialogOpen(false);
              handleSaveDepthChart();
            }}
            variant="contained"
            disabled={!chartName.trim() || !season.trim()}
            startIcon={<SaveIcon />}
          >
            {isNewChart ? "Create" : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DepthChartEditor;
